import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { cn } from '@/lib/utils'
import type { MoveNodeData } from '@/components/series/MoveNode'

/**
 * The Series-graph canvas node, inside the mobile WebView only.
 *
 * `MoveNode` stays the web app's own series page's node — the two are
 * registered separately (this one at SeriesGraphEmbedPage.tsx's nodeTypes,
 * that one at SeriesDetailPage.tsx:72-74) and only this one is on Tribe.
 *
 * `isConnectable` is forwarded to both handles because React Flow's Handle
 * defaults it to true and never reads the store itself
 * (@xyflow/react@12.10.0, dist/esm/index.js:1734, :1843). Without the
 * forward the `connectable` class is unconditional and the view-mode dim in
 * series-graph-embed.css can never match.
 *
 * NODE_WIDTH/NODE_HEIGHT_BASE/STRIP_HEIGHT/NODE_HEIGHT are a PAIRED CONSTANT
 * with the server's `PLACEMENT_BOX` in
 * `white-gi-api/src/series-moves/series-moves.service.ts` — see
 * docs/BACKEND_CONVENTIONS.md → "Constants paired with a client". The root
 * div is given an explicit height equal to NODE_HEIGHT (thumbnail) or
 * NODE_HEIGHT_BASE (no thumbnail). NODE_HEIGHT_BASE has no room for a
 * placeholder box: it is padding plus a title plus, optionally, a chip —
 * nothing else — which is why the no-thumbnail branch renders none. There
 * is no description line either. Both branches' actual content fits the
 * height they're pinned to, so the rendered box IS the constant rather than
 * an estimate of it — that is what makes the pairing checkable at all.
 *
 * The width is an inline style rather than a Tailwind class so NODE_WIDTH is
 * its only source: `w-[176px]` would have to be a literal, because Tailwind's
 * JIT scanner reads class strings statically and cannot see `w-[${NODE_WIDTH}px]`.
 */

export const NODE_WIDTH = 176
export const NODE_HEIGHT_BASE = 118 // the frame's node with no thumbnail
export const STRIP_HEIGHT = 99 // a full-bleed 16:9 strip at NODE_WIDTH
export const NODE_HEIGHT = NODE_HEIGHT_BASE + STRIP_HEIGHT // 217 — the tallest state

interface TribeMoveNodeProps {
  data: MoveNodeData
  selected?: boolean
  isConnectable?: boolean
}

export const TribeMoveNode = memo(function TribeMoveNode({
  data,
  selected,
  isConnectable,
}: TribeMoveNodeProps) {
  return (
    <div
      className={cn(
        'relative box-border border',
        'rounded-[var(--tribe-radius-sm)] bg-[var(--tribe-color-surface3)]',
        selected
          ? 'border-[var(--tribe-color-borderActive)]'
          : 'border-[var(--tribe-color-neutral5)] hover:border-[var(--tribe-color-borderSecondary)]'
      )}
      style={{
        width: NODE_WIDTH,
        height: data.thumbnailUrl ? NODE_HEIGHT : NODE_HEIGHT_BASE,
        touchAction: 'none',
      }}
      role="button"
      tabIndex={0}
      aria-label={`Move: ${data.name}. Click to view details.`}
      data-testid="tribe-move-node"
    >
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        aria-label="Connection target"
      />
      {data.thumbnailUrl ? (
        <div
          className="w-full overflow-hidden rounded-t-[var(--tribe-radius-sm)] bg-[var(--tribe-color-neutral4)]"
          style={{ height: STRIP_HEIGHT }}
        >
          <img
            src={data.thumbnailUrl}
            alt={data.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : null}
      <div className="px-[var(--tribe-spacing-6)] py-[var(--tribe-spacing-4)]">
        <h3
          className="line-clamp-2 text-[var(--tribe-color-neutral11)]"
          style={{
            fontFamily: 'var(--tribe-text-body-bold-family)',
            fontWeight: 'var(--tribe-text-body-bold-weight)',
            fontSize: 'var(--tribe-text-body-bold-size)',
            lineHeight: 'var(--tribe-text-body-bold-lineHeight)',
            letterSpacing: 'var(--tribe-text-body-bold-letterSpacing)',
          }}
          title={data.name}
        >
          {data.name}
        </h3>
        {data.tagName ? (
          <span
            data-testid="tribe-move-node-tag"
            className="mt-[var(--tribe-spacing-1)] inline-block rounded-[var(--tribe-radius-xs)] bg-[var(--tribe-color-neutral6)] px-[6px] py-[var(--tribe-spacing-1)] uppercase text-[var(--tribe-color-brandSecondary)]"
            style={{
              fontFamily: 'var(--tribe-text-label-family)',
              fontWeight: 'var(--tribe-text-label-weight)',
              fontSize: 'var(--tribe-text-label-size)',
              lineHeight: 'var(--tribe-text-label-lineHeight)',
              letterSpacing: 'var(--tribe-text-label-letterSpacing)',
            }}
          >
            {data.tagName}
          </span>
        ) : null}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        aria-label="Connection source"
      />
    </div>
  )
})

TribeMoveNode.displayName = 'TribeMoveNode'
